import { J2000 } from "./constants";

/**
 * Julian Date utilities.
 *
 * Julian Date is a continuous count of days since the beginning of the
 * Julian Period (January 1, 4713 BC, proleptic Julian calendar). It is
 * the standard time representation in astronomy and avoids calendar
 * irregularities.
 */

/**
 * Convert a calendar date to Julian Date.
 * Uses the algorithm from Meeus, "Astronomical Algorithms", valid for
 * dates after 4800 BC.
 *
 * @param year  Calendar year
 * @param month Month (1-12)
 * @param day   Day of month
 * @param hour  Hour (0-23), defaults to 12 (noon)
 * @param minute Minute (0-59)
 * @param second Second (fractional allowed)
 */
export function fromCalendar(
  year: number,
  month: number,
  day: number,
  hour: number = 12,
  minute: number = 0,
  second: number = 0.0,
): number {
  const a = Math.trunc((14 - month) / 12);
  const y = year + 4800 - a;
  const m = month + 12 * a - 3;

  const jdn =
    day +
    Math.trunc((153 * m + 2) / 5) +
    365 * y +
    Math.trunc(y / 4) -
    Math.trunc(y / 100) +
    Math.trunc(y / 400) -
    32045;

  const fraction = (hour - 12) / 24.0 + minute / 1440.0 + second / 86400.0;

  return jdn + fraction;
}

/**
 * Result of converting a Julian Date to a calendar date.
 */
export interface CalendarDate {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

/**
 * Convert Julian Date to calendar date components.
 * Algorithm from Meeus.
 */
export function toCalendar(jd: number): CalendarDate {
  const jdp = jd + 0.5;
  const z = Math.floor(jdp);
  const f = jdp - z;

  let a: number;
  if (z < 2299161) {
    a = z;
  } else {
    const alpha = Math.trunc((z - 1867216.25) / 36524.25);
    a = z + 1 + alpha - Math.trunc(alpha / 4);
  }

  const b = a + 1524;
  const c = Math.trunc((b - 122.1) / 365.25);
  const d = Math.trunc(365.25 * c);
  const e = Math.trunc((b - d) / 30.6001);

  const day = b - d - Math.trunc(30.6001 * e);
  const month = e < 14 ? e - 1 : e - 13;
  const year = month > 2 ? c - 4716 : c - 4715;

  const dayFraction = f * 24.0;
  const hour = Math.trunc(dayFraction);
  const minuteFraction = (dayFraction - hour) * 60.0;
  const minute = Math.trunc(minuteFraction);
  const second = (minuteFraction - minute) * 60.0;

  return { year, month, day, hour, minute, second };
}

/**
 * Convert a JavaScript Date to Julian Date.
 */
export function fromDate(date: Date): number {
  return fromCalendar(
    date.getUTCFullYear(),
    date.getUTCMonth() + 1,
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds() + date.getUTCMilliseconds() / 1000.0,
  );
}

/**
 * Convert a Julian Date to a JavaScript Date (UTC).
 */
export function toDate(jd: number): Date {
  const cal = toCalendar(jd);
  const sec = Math.trunc(cal.second);
  const ms = Math.round((cal.second - sec) * 1000);
  return new Date(Date.UTC(cal.year, cal.month - 1, cal.day, cal.hour, cal.minute, sec, ms));
}

/**
 * Julian centuries since J2000.0 epoch.
 * Used for precession and nutation calculations.
 */
export function centuriesSinceJ2000(jd: number): number {
  return (jd - J2000) / 36525.0;
}

/**
 * Days since J2000.0 epoch.
 */
export function daysSinceJ2000(jd: number): number {
  return jd - J2000;
}
